import { DateTime } from 'luxon';
import React from 'react';

import { LocalizedTime } from '../../helpers/dates';
import { OpenCloseHoursWeekdays } from './open-close-hours-weekdays.interface';
import { OpeningHoursCompact } from './opening-hours-compact';
import { OpeningHoursCompactBlock } from './opening-hours-compact-block';
import { OpeningHoursExceptions } from './opening-hours-exceptions';
import { OpeningHoursFull } from './opening-hours-full';
import { OpeningHoursToday } from './opening-hours-today';
import { ILibraryOpeningHours } from '@/lib/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '@/lib/fragments/libraryOpeningHoursException';

interface OpeningHoursProps {
  openingHours: ILibraryOpeningHours;
  openingHoursExceptions: ILibraryOpeningHoursException[];
  timezone: string;
  node_locale: string;
  variant?:
  | 'full'
  | 'compact'
  | 'today'
  | 'tenant-exceptions'
  | 'block-compact'
  | 'block-compact-exceptions';
  openingHoursClosed: string;
  openingHoursOpenToday: string;
  openingHoursClosedToday: string;
  openingHoursBlockExceptionTitle?: string;
  showMore?: string;
  showLess?: string;
}
export const OpeningHours = ({
  openingHours,
  openingHoursExceptions,
  timezone,
  node_locale,
  variant,
  openingHoursClosed,
  openingHoursClosedToday,
  openingHoursOpenToday,
  openingHoursBlockExceptionTitle,
  showMore,
  showLess,
}: OpeningHoursProps) => {
  if (!openingHours || !node_locale || !timezone) return null;
  const todayDateTimeInMeetingplaceTimezone = DateTime.now()
    .setLocale(node_locale)
    .setZone(timezone);

  const todaysWeekdayNumber =
    Number(todayDateTimeInMeetingplaceTimezone.weekday.toFixed()) - 1;

  const openingHoursArray: Array<OpenCloseHoursWeekdays> = [
    {
      weekDays: [1],
      open: openingHours?.mondayOpen,
      close: openingHours?.mondayClose,
    },
    {
      weekDays: [2],
      open: openingHours?.tuesdayOpen,
      close: openingHours?.tuesdayClose,
    },
    {
      weekDays: [3],
      open: openingHours?.wednesdayOpen,
      close: openingHours?.wednesdayClose,
    },
    {
      weekDays: [4],
      open: openingHours?.thursdayOpen,
      close: openingHours?.thursdayClose,
    },
    {
      weekDays: [5],
      open: openingHours?.fridayOpen,
      close: openingHours?.fridayClose,
    },
    {
      weekDays: [6],
      open: openingHours?.saturdayOpen,
      close: openingHours?.saturdayClose,
    },
    {
      weekDays: [7],
      open: openingHours?.sundayOpen,
      close: openingHours?.sundayClose,
    },
  ];

  const todaysExceptionStrings = openingHoursExceptions
    ?.map((exceptionDate: any) => {
      const isClosed =
        exceptionDate?.closes === 'Closed' || exceptionDate?.opens === 'Closed';

      const exceptionDateTime = DateTime.fromISO(exceptionDate?.date).setLocale(
        node_locale,
      );

      if (
        exceptionDateTime.hasSame(
          todayDateTimeInMeetingplaceTimezone,
          'month',
        ) &&
        exceptionDateTime.hasSame(todayDateTimeInMeetingplaceTimezone, 'day')
      ) {
        // Todays date matches an exception date - return a formatted string (?)
        const returnString = isClosed
          ? `${exceptionDate?.name}: ${openingHoursClosed}`
          : `${exceptionDate?.name}:
        ${LocalizedTime(exceptionDate?.opens, node_locale)} -
        ${LocalizedTime(exceptionDate?.closes, node_locale)}`;
        return returnString;
      }
      return '';
    })
    ?.filter((exceptionString: any) => exceptionString !== '');

  //Display ordinary opening hours - followed by a list of all exceptions
  if (variant && variant === 'block-compact-exceptions') {
    const allExceptionStrings: string[][] = openingHoursExceptions
      ?.map((exceptionDate: any) => {
        return {
          ...exceptionDate,
          date: DateTime.fromISO(exceptionDate?.date).startOf('day'),
        };
      })
      ?.sort((a: any, b: any) => a?.date - b?.date)
      ?.filter((exceptionDate: any) => {
        const today = DateTime.now().startOf('day');
        return exceptionDate?.date >= today;
      })
      ?.map((exceptionDate: any) => {
        const isClosed =
          exceptionDate?.closes === 'Closed' || exceptionDate?.opens === 'Closed';

        const exceptionDateTime = exceptionDate?.date.setLocale(node_locale);

        const returnStringArray = isClosed
          ? [
            `${exceptionDate?.name}, ${exceptionDateTime.toFormat(
              'd.M.yyyy',
            )}`,
            openingHoursClosed,
          ]
          : [
            `${exceptionDate?.name}, ${exceptionDateTime.toFormat(
              'd.M.yyyy',
            )}`,
            `${LocalizedTime(
              exceptionDate?.opens,
              node_locale,
            )} - ${LocalizedTime(exceptionDate?.closes, node_locale)}`,
          ];

        return returnStringArray;
      });

    return (
      <OpeningHoursCompactBlock
        node_locale={node_locale}
        timezone={timezone}
        openingHoursArray={openingHoursArray}
        openingHoursClosed={openingHoursClosed}
        exceptionStrings={allExceptionStrings}
        exceptionTitle={openingHoursBlockExceptionTitle}
      />
    );
  }

  //Display today's ordinary opening hours - used in block for tenants
  if (variant && variant === 'block-compact') {
    return (
      <OpeningHoursCompactBlock
        node_locale={node_locale}
        timezone={timezone}
        openingHoursArray={openingHoursArray}
        openingHoursClosed={openingHoursClosed}
      />
    );
  }

  if (variant && variant === 'tenant-exceptions') {
    const next30Days = todayDateTimeInMeetingplaceTimezone.plus({ days: 30 });

    const allExceptionStrings: string[][] = openingHoursExceptions
      ?.map((exceptionDate: any) => {
        return {
          ...exceptionDate,
          date: DateTime.fromISO(exceptionDate?.date),
        };
      })
      ?.filter((exceptionDate: any) => {
        const today = DateTime.now().startOf('day');
        return (
          exceptionDate?.date.hasSame(
            todayDateTimeInMeetingplaceTimezone,
            'day',
          ) ||
          (exceptionDate?.date >= today &&
            exceptionDate?.date > todayDateTimeInMeetingplaceTimezone &&
            exceptionDate?.date < next30Days)
        );
      })
      ?.sort((a: any, b: any) => a?.date - b?.date)
      ?.map((exceptionDate: any) => {
        const isClosed =
          exceptionDate?.closes === 'Closed' || exceptionDate?.opens === 'Closed';

        const exceptionDateTime = exceptionDate?.date.setLocale(node_locale);

        const returnStringArray = isClosed
          ? [
            `${exceptionDate?.name}, ${exceptionDateTime.toFormat(
              'd.M.yyyy',
            )}`,
            openingHoursClosed,
          ]
          : [
            `${exceptionDate?.name}, ${exceptionDateTime.toFormat(
              'd.M.yyyy',
            )}`,
            `${LocalizedTime(
              exceptionDate?.opens,
              node_locale,
            )} - ${LocalizedTime(exceptionDate?.closes, node_locale)}`,
          ];

        return returnStringArray;
      });
    return (
      <OpeningHoursExceptions
        exceptionStrings={allExceptionStrings}
        showMoreText={showMore}
        showLessText={showLess}
      />
    );
  }

  //Display exception instead of today's opening hours
  if (todaysExceptionStrings && todaysExceptionStrings.length > 0) {
    return (
      <OpeningHoursExceptions todaysExceptionStrings={todaysExceptionStrings} />
    );
  }

  //Used in header & footer
  if (variant && variant === 'today') {
    return (
      <OpeningHoursToday
        openingHoursClosedToday={openingHoursClosedToday}
        openingHoursOpenToday={openingHoursOpenToday}
        openingHours={openingHoursArray[todaysWeekdayNumber]}
        node_locale={node_locale}
      />
    );
  }

  //Used in footer
  if (variant && variant === 'compact') {
    return (
      <OpeningHoursCompact
        node_locale={node_locale}
        timezone={timezone}
        openingHoursArray={openingHoursArray}
        openingHoursClosed={openingHoursClosed}
      />
    );
  }

  if (!variant || (variant && variant === 'full')) {
    return (
      <OpeningHoursFull
        node_locale={node_locale}
        openingHoursArray={openingHoursArray}
        timezone={timezone}
      />
    );
  }

  return null;
};
